import { CSSProperties, useEffect, useRef, useState } from 'react';
import { Color, Size } from '@vivli/shared/theme';
import { CheckboxComponent, DatePickerComponent, GoBackLinkComponent, TextAreaFormFieldComponent } from '@vivli/shared/components';
import { IOrganization } from '@vivli/features/organizations/infrastructure/interface';
import { IUserDetails } from '@vivli/features/users/infrastructure/interface';
import { useUserInfoContext } from '@vivli/features/users/infrastructure/context';
import { ConfirmButtonView } from '@vivli/features/users/components';
import { finalize, map } from 'rxjs/operators';
import { useCleanupHook } from '@vivli/shared/infrastructure/hook';
import { useActiveUserContext, useModalApiContext, useToastApiContext, useUsersApiContext } from '@vivli/core/infrastructure/context';
import { OrgMembershipsGridComponent } from './org-memberships-grid.component';
import { AssociatedDataRequestsGridComponent } from './associated-data-requests-grid.component';
import { useFormContext, useFormState } from 'react-hook-form';
import { useUserPermissions } from '@vivli/features/users/infrastructure/hook';
import { AssociatedEnquiriesGridComponent } from './associated-enquiries-grid.component';
import { Observable } from 'rxjs';
import { LoadIndicatorColorEnum, PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { useParams } from 'react-router';
import { ButtonView, LoadIndicatorView } from '@vivli/shared/views';

const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    paddingBottom: 50,
    height: '100%',
    padding: 15,
    color: Color.BLACK,
    minWidth: 300
};

const headerStyle: CSSProperties = {
    padding: '15px 20px',
    borderBottom: '1px solid white',
    fontSize: Size.FontSize.Larger,
    alignContent: 'center'
};

const bodyContainerStyle: CSSProperties = {
    padding: 20,
    height: '100%',
    fontSize: Size.FontSize.Medium
};

const bodyStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgb(233, 234, 239)',
    paddingLeft: Size.PADDING,
    paddingRight: Size.PADDING,
    paddingTop: Size.PADDING,
    paddingBottom: Size.PADDING,
    color: 'black',
    overflowY: 'scroll',
    height: '90%'
};

const sectionStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    paddingRight: '25px',
    paddingLeft: '25px',
    justifyContent: 'space-between'
};

const section2Style: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
};

const sectionContainerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column'
};

const rowStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row'
};

const columnStyle: CSSProperties = {
    fontSize: 20,
    padding: 10
};

const fieldStyle: CSSProperties = {
    fontSize: 20,
    marginRight: 20,
    paddingTop: 6
};

const buttonStyle: CSSProperties = {
    backgroundColor: Color.VIVLI_BLUE,
    marginBottom: 'auto',
    height: 34,
    fontSize: 13,
    marginRight: -20
};

const sectionHeaderStyle: CSSProperties = {
    fontSize: 22,
    display: 'flex',
    marginTop: '25px',
    marginBottom: '15px',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px 10px 0 10px'
};

const buttonFieldStyle: CSSProperties = {
    minWidth: '150px'
};

const goBackButtonStyle: CSSProperties = {
    display: 'flex',
    paddingLeft: '20px',
    justifyContent: 'flex-start',
    marginBottom: '5px'
};

const dateComponentStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px'
};

const notesFieldStyle: CSSProperties = {
    paddingRight: '25px',
    paddingLeft: '25px',
    display: 'flex',
    gap: '10px',
    flexDirection: 'column'
};
const saveNoteButton: CSSProperties = {
    marginTop: '10px',
    display: 'flex',
    justifyContent: 'flex-end'
};
const loadIndicatorStyle: CSSProperties = {
    position: 'absolute',
    left: '80%',
    top: '43%',
    transform: 'translate(-50%, -50%)'
};

interface UserDetailsComponentProps {
    currentUser: IUserDetails;
    organizations: IOrganization[];
}

export const UserDetailsComponent = ({ currentUser, organizations }: UserDetailsComponentProps) => {
    useCleanupHook();
    const [completedDate, setCompletedDate] = useState<Date | string>(null);

    const { user: activeUser, hasPermission } = useActiveUserContext();
    const [user, setUser] = useState<IUserDetails>(currentUser);
    const [newPassword, setNewPassword] = useState<string>(null);
    //TODO PERMFE - redo with permissions+
    const isVivliAdmin = hasPermission(PermissionsEnum.EditUserAdv);
    const { isOrgOrVivliAdmin } = useUserPermissions();
    const newPasswordRef = useRef<HTMLInputElement>();
    const usersService = useUsersApiContext();
    const toastService = useToastApiContext();
    const { handleUpdateUserInfo, isSavingNotes, userDataRequests, userEnquiries } = useUserInfoContext();
    const { isDirty } = useFormState();
    const modalService = useModalApiContext();
    const [isLoading, setIsLoading] = useState(false);
    const { userId } = useParams();

    const handleCompletedDateChange = (completedDate, value) => {
        // this date string format appears to keep the database on the selected date (just barely)
        const usableDateTime = value.toLocaleDateString('sv');
        return usersService.updateDaTrainingCompletedDate(user.id, usableDateTime).subscribe({
            next: (result) => {
                const updatedUser = {
                    ...user,
                    userRole: {
                        ...user.userRole,
                        daTrainingCompletedDate: value
                    }
                };
                setUser(updatedUser);
                const message = `Successfully set training completion date for ${user.displayName}`;
                toastService.success(message);
            },
            error: () => {
                const message = `Error encountered setting training completion date for ${user.displayName}`;
                toastService.success(message);
            }
        });
    };

    const copyPasswordToClipboard = () => {
        navigator.clipboard.writeText(newPassword).then(() => {
            toastService.success('Password copied to clipboard.');
        });
    };

    const updateUserStatus = () => {
        const newStatus = !user.disabled;
        return usersService.setUserStatus(user.id, newStatus).pipe(
            map(() => {
                const updatedUser = { ...user, disabled: newStatus };

                setUser(updatedUser);

                const message = newStatus ? `Successfully disabled ${user.displayName}` : `Successfully enabled ${user.displayName}`;
                toastService.success(message);
            })
        );
    };

    const updateVivliAdminStatus = () => {
        //TODO PERMFE +
        const newStatus = !user.isVivliAdmin;
        return usersService.setVivliAdminPermission(user.id, newStatus).pipe(
            map(() => {
                const updatedUser = {
                    ...user,
                    isVivliAdmin: newStatus,
                    userRole: {
                        ...user.userRole,
                        isVivliAdmin: newStatus
                    }
                };

                setUser(updatedUser);

                const message = newStatus
                    ? `Successfully made ${user.displayName} a Vivli Admin`
                    : `Successfully revoked Vivli Admin permission from ${user.displayName}`;
                toastService.success(message);
            })
        );
    };

    const updateUserDataAccessTraining = () => {
        const newTrainingStatus = !user.userRole.isDaTrainingComplete; //set this value to show the right state in the checkbox after the update
        return usersService.setUserDaTraining(user.id, newTrainingStatus).pipe(
            map(() => {
                const updatedUser = {
                    ...user,
                    isDaTrainingComplete: newTrainingStatus,
                    userRole: {
                        ...user.userRole,
                        isDaTrainingComplete: newTrainingStatus,
                        daTrainingCompletedDate: newTrainingStatus ? new Date() : null
                    }
                };

                updatedUser.userRole.isDaTrainingComplete
                    ? setCompletedDate(updatedUser.userRole.daTrainingCompletedDate)
                    : setCompletedDate(null);

                setUser(updatedUser);

                const message = newTrainingStatus
                    ? `Successfully set training status for ${user.displayName}`
                    : `Successfully unset training status for ${user.displayName}`;
                toastService.success(message);
            })
        );
    };

    const resetUserPassword = () =>
        usersService.resetUserPassword(user.id).pipe(
            map((password) => {
                toastService.success(`Successfully reset password for ${user.displayName}`);
                return password;
            })
        );

    const handleResetPasswordError = (err) => {
        toastService.error('Unable to reset password, please try again.');
    };

    const showConfirmModal = (confirmMessage: string, apiCall: () => Observable<any>, errorMessage: string) => {
        modalService.confirm(confirmMessage, {
            onConfirm: () => {
                if (apiCall) {
                    setIsLoading(true);
                    apiCall()
                        .pipe(finalize(() => setIsLoading(false)))
                        .subscribe(
                            (apiResult) => {
                                setIsLoading(false);
                            },
                            (error) => {
                                setIsLoading(false);
                                modalService.error(errorMessage + error);
                            }
                        );
                }
            }
        });
    };

    useEffect(() => {
        //TODO PERMFE - redo with permissions+
        if (!activeUser || !isVivliAdmin) {
            //org admin takes data form Current User - no changes allowed, we don't need to refresh
            return;
        }
        //get the user from userId
        usersService.getUserDetails(userId).subscribe(
            (newUserDetails) => {
                newUserDetails?.userRole.isDaTrainingComplete
                    ? setCompletedDate(newUserDetails?.userRole.daTrainingCompletedDate)
                    : setCompletedDate(null);
                setUser(newUserDetails);
            },
            (error) => {
                modalService.error(`Cannot get user information: ${error}`);
            }
        );
    }, [userId]);

    return (
        <div style={containerStyle}>
            {user && organizations && (
                <div style={{ height: '100%' }}>
                    <div style={goBackButtonStyle}>
                        <GoBackLinkComponent destination={'/admin/users/'} />
                    </div>
                    <div data-test-id="user-detail-header" style={headerStyle}>
                        User Details - {user.displayName} {user.roleName}
                    </div>
                    <div style={bodyContainerStyle}>
                        <div style={bodyStyle} className="scrolly">
                            <div>
                                <div style={sectionStyle}>
                                    <div>
                                        <div data-test-id="user-detail-display-name" style={{ ...rowStyle, marginRight: 20 }}>
                                            <div style={columnStyle}>Display Name:</div>
                                            <div style={columnStyle}>{user.displayName}</div>
                                        </div>
                                        <div data-test-id="user-detail-email" style={rowStyle}>
                                            <div style={columnStyle}>Email:</div>
                                            <div style={columnStyle}>{user.email}</div>
                                        </div>
                                        <div data-test-id="user-detail-last-login" style={rowStyle}>
                                            <div style={columnStyle}>Days Since Last Login:</div>
                                            <div style={columnStyle}>{user.daysSinceLastLogin}</div>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            ...sectionContainerStyle,
                                            display: isOrgOrVivliAdmin && !isVivliAdmin ? 'none' : 'block'
                                        }}
                                    >
                                        {isLoading && (
                                            <div>
                                                <LoadIndicatorView color={LoadIndicatorColorEnum.Blue} style={loadIndicatorStyle} />
                                            </div>
                                        )}
                                        <div data-test-id="user-detail-update-user-status" style={{ ...section2Style }}>
                                            <div style={{ ...fieldStyle, paddingTop: 10, marginRight: 0 }}>User Account Enabled</div>
                                            <div>
                                                <CheckboxComponent
                                                    label={''}
                                                    checked={!user.disabled}
                                                    onChange={(event) =>
                                                        showConfirmModal(
                                                            user.disabled
                                                                ? 'Are you sure you want to enable this user?'
                                                                : 'Are you sure you want to disable this user?',
                                                            updateUserStatus,
                                                            'Unable to update user status, please try again.'
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                        {
                                            //TODO PERMFE - redo with permissions+
                                            hasPermission(PermissionsEnum.UseAdmConsoleAdv) && (
                                                <div
                                                    data-test-id="user-detail-admin-permission"
                                                    style={{ ...section2Style, paddingTop: 0 }}
                                                >
                                                    <div style={{ ...fieldStyle, paddingTop: 11, marginRight: 0 }}>Vivli Admin</div>
                                                    <div>
                                                        <CheckboxComponent
                                                            label={''}
                                                            checked={user.isVivliAdmin}
                                                            onChange={(event) =>
                                                                showConfirmModal(
                                                                    user.isVivliAdmin
                                                                        ? 'Are you sure you want to revoke the Vivli Admin permission from this user?'
                                                                        : 'Are you sure you want to make this user a Vivli Admin?',
                                                                    updateVivliAdminStatus,
                                                                    'Unable to update user Vivli Admin status, please try again.'
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            )
                                        }
                                        {
                                            //TODO PERMFE - redo with permissions+
                                            isVivliAdmin && (
                                                <div
                                                    data-test-id="user-detail-da-training-complete"
                                                    style={{ ...section2Style, paddingTop: 0 }}
                                                >
                                                    <div style={{ ...fieldStyle, paddingTop: 10, marginRight: 0 }}>
                                                        Data Access Training Completed
                                                    </div>
                                                    <div>
                                                        <CheckboxComponent
                                                            label={''}
                                                            checked={user.userRole.isDaTrainingComplete}
                                                            onChange={(event) =>
                                                                showConfirmModal(
                                                                    user.userRole.isDaTrainingComplete
                                                                        ? 'Are you sure this user has not completed training?'
                                                                        : 'Are you sure this user has completed training?',
                                                                    updateUserDataAccessTraining,
                                                                    'Unable to update user status, please try again.'
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            )
                                        }

                                        <div style={dateComponentStyle}>
                                            <div style={{ ...rowStyle, marginRight: 14 }}>
                                                <div style={{ width: '45%', fontSize: 20, paddingTop: 5, marginRight: 2 }}>
                                                    Training Date:
                                                </div>
                                                <DatePickerComponent
                                                    style={{ width: '35%' }}
                                                    onChange={(event) => {
                                                        handleCompletedDateChange('isDaTrainingComplete', event);
                                                        setCompletedDate(event);
                                                    }}
                                                    readonly={!user.userRole.isDaTrainingComplete}
                                                    isClearable={false}
                                                    value={completedDate}
                                                />
                                            </div>
                                        </div>
                                        <div data-test-id="user-detail-password" style={{ ...section2Style, paddingTop: 10 }}>
                                            <div style={buttonFieldStyle}>
                                                <input type="text" style={{ display: 'none' }} ref={newPasswordRef} />
                                                {newPassword ? (
                                                    <ButtonView style={buttonStyle} onClick={copyPasswordToClipboard}>
                                                        Copy Password to Clipboard
                                                    </ButtonView>
                                                ) : (
                                                    <ConfirmButtonView
                                                        confirmMessage="Reset Password?"
                                                        apiCall={resetUserPassword}
                                                        callback={(confirmed, result) => confirmed && setNewPassword(result)}
                                                        callbackError={handleResetPasswordError}
                                                    >
                                                        {newPassword ? 'New Password' : 'Reset Password'}
                                                    </ConfirmButtonView>
                                                )}
                                            </div>
                                        </div>

                                        {isVivliAdmin && (
                                            <div style={saveNoteButton}>
                                                <ButtonView
                                                    style={{
                                                        minWidth: '150px',
                                                        backgroundColor: Color.ERROR_RED,
                                                        fontSize: 13
                                                    }}
                                                    onClick={() => handleUpdateUserInfo()}
                                                    isLoading={isSavingNotes}
                                                    disabled={!isDirty}
                                                >
                                                    Save Notes
                                                </ButtonView>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {isVivliAdmin && (
                                    <div style={notesFieldStyle}>
                                        <div style={{ fontSize: 22 }}>Notes</div>
                                        <TextAreaFormFieldComponent name={'notes'} style={{ height: '55px', width: '100%' }} rows={4} />
                                    </div>
                                )}

                                <div>
                                    <div style={sectionHeaderStyle}>Org Memberships</div>
                                    {user.userRole.orgMemberships?.length > 0 ? (
                                        <div data-test-id="user-detail-org-membership-grid">
                                            <OrgMembershipsGridComponent user={user} setUser={setUser} organizations={organizations} />
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                padding: 40,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                marginBottom: 10
                                            }}
                                        >
                                            No Organization Memberships Found
                                        </div>
                                    )}
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={sectionHeaderStyle}>Associated Data Requests</div>
                                    {userDataRequests ? (
                                        <div data-test-id="user-detail-associated-dataRequest-grid">
                                            <AssociatedDataRequestsGridComponent
                                                userDataRequests={userDataRequests}
                                                isVivliAdmin={isVivliAdmin}
                                            />
                                        </div>
                                    ) : (
                                        <LoadIndicatorView style={{ margin: 'auto', marginTop: '10px' }} />
                                    )}
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={sectionHeaderStyle}>Associated Enquiries</div>
                                    {userEnquiries ? (
                                        <div data-test-id="user-detail-associated-enquiry-grid">
                                            <AssociatedEnquiriesGridComponent userEnquiries={userEnquiries} />
                                        </div>
                                    ) : (
                                        <LoadIndicatorView style={{ margin: 'auto', marginTop: '10px' }} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
